<template>
  <section id="resend-to-rxnt">
    <div class="container-sm">
      <div class="upload-card" v-loading="loading">
        <div class="upload-card__header">
          <h2>Upload Mileage Cases</h2>
          <p>Upload an Excel file with "Patient ID(PID-2)" and "Placer Order Number(OBR-2)" columns to process mileage cases</p>
          <div class="example-columns">
            <p class="example-title">Required columns:</p>
            <div class="table-scroll">
            <table class="example-table">
              <thead>
                <tr>
                  <th>Patient ID(PID-2)</th>
                  <th>Placer Order Number(OBR-2)</th>
                  <th>ICD1</th>
                  <th>ICD2</th>
                  <th>ICD3</th>
                  <th>ICD4</th>
                  <th>Transaction Code(FT1-7)</th>
                  <th>Transaction Quantity(FT1-10)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>TDY260801003</td>
                  <td>TDY260801003*M</td>
                  <td>Z00.00</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>P9603</td>
                  <td>14</td>
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        </div>

        <div class="upload-card__body">
          <el-upload
            ref="uploadRef"
            class="upload-area"
            drag
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleFileChange"
            accept=".xlsx,.xls,.csv"
          >
            <div class="upload-content" v-if="!selectedFile">
              <div class="upload-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <div class="upload-text">
                <p class="main-text">Drop your Excel file here or <span>browse</span></p>
                <p class="sub-text">Supports: .xlsx, .xls, .csv</p>
              </div>
            </div>
            <div class="file-selected" v-else>
              <div class="file-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <div class="file-details">
                <p class="file-name">{{ selectedFile.name }}</p>
                <p class="file-size">{{ formatFileSize(selectedFile.size) }}</p>
              </div>
              <el-button type="danger" circle size="small" @click.stop="clearFile" class="remove-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </el-button>
            </div>
          </el-upload>

          <el-button
            type="primary"
            size="large"
            class="process-btn"
            :disabled="!selectedFile"
            @click="uploadFile"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 8px;">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
            Upload & Process
          </el-button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "@/config/axios";

export default {
  name: "ResendToRxnt",
  data() {
    return {
      loading: false,
      selectedFile: null,
      uploadResult: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["getAuthenticationDetails"]),
  },
  methods: {
    handleFileChange(file) {
      // Validate file type
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const validExtensions = ["xlsx", "xls", "csv"];

      if (!validExtensions.includes(fileExtension)) {
        this.$message.error("Please upload an Excel file (.xlsx, .xls) or CSV file");
        return;
      }

      this.selectedFile = file.raw;
      this.uploadResult = null;
    },
    clearFile() {
      this.selectedFile = null;
      this.uploadResult = null;
      if (this.$refs.uploadRef) {
        this.$refs.uploadRef.clearFiles();
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
    async uploadFile() {
      if (!this.selectedFile) {
        this.$message.warning("Please select a file first");
        return;
      }

      this.loading = true;
      this.uploadResult = null;

      try {
        const formData = new FormData();
        formData.append("accession_file", this.selectedFile);

        const response = await axios.post(
          "/cpt/resend-mileage-rxnt-excel",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        this.uploadResult = {
          success: true,
          message: "File uploaded successfully",
        };

        this.$message({
          message: "File uploaded - processing started in background",
          type: "success",
          duration: 5000,
        });
        this.selectedFile = null;
        if (this.$refs.uploadRef) {
          this.$refs.uploadRef.clearFiles();
        }
      } catch (error) {
        console.error("Upload error:", error);
        this.uploadResult = {
          success: false,
          message:
            error.response?.data?.message ||
            error.message ||
            "Failed to upload file",
        };
        this.$message({
          message: this.uploadResult.message,
          type: "error",
          duration: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#resend-to-rxnt {
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);

  .container-sm {
    width: 100%;
    max-width: 640px;
  }

  .upload-card {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    &__header {
      text-align: center;
      padding: 15px 15px 10px;
      border-bottom: 1px solid #f0f0f0;

      h2 {
        margin: 0 0 4px;
        font-size: 16px;
        font-weight: 600;
        color: #1b2459;
      }

      p {
        margin: 0;
        font-size: 11px;
        color: #909399;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .example-columns {
        margin-top: 10px;
        text-align: left;

        .example-title {
          font-size: 10px;
          font-weight: 600;
          color: #606266;
          margin: 0 0 4px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .table-scroll {
          overflow-x: auto;
        }

        .example-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;

          th, td {
            border: 1px solid #ebeef5;
            padding: 3px 8px;
            white-space: nowrap;
          }

          th {
            background: #f5f7fa;
            color: #909399;
            font-weight: 600;
          }

          td {
            color: #606266;
          }
        }
      }
    }

    &__body {
      padding: 15px;
      display: flex;
      flex-direction: column;
      align-items: center;

      .upload-area {
        width: 100%;

        :deep(.el-upload) {
          width: 100%;
        }

        :deep(.el-upload-dragger) {
          width: 100%;
          height: auto;
          min-height: 100px;
          border: 2px dashed #d9d9d9;
          border-radius: 12px;
          background: #fafafa;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;

          &:hover {
            border-color: #409eff;
            background: #f0f7ff;
          }
        }
      }

      .upload-content {
        text-align: center;
        padding: 20px;

        .upload-icon {
          color: #c0c4cc;
          margin-bottom: 16px;
          transition: color 0.3s ease;
        }

        &:hover .upload-icon {
          color: #409eff;
        }

        .upload-text {
          .main-text {
            font-size: 16px;
            color: #606266;
            margin: 0 0 8px;

            span {
              color: #409eff;
              font-weight: 500;
              cursor: pointer;
            }
          }

          .sub-text {
            font-size: 13px;
            color: #909399;
            margin: 0;
          }
        }
      }

      .file-selected {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 20px;
        width: 100%;

        .file-icon {
          color: #67c23a;
          flex-shrink: 0;
        }

        .file-details {
          flex: 1;
          min-width: 0;

          .file-name {
            font-size: 15px;
            font-weight: 500;
            color: #303133;
            margin: 0 0 4px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .file-size {
            font-size: 13px;
            color: #909399;
            margin: 0;
          }
        }

        .remove-btn {
          flex-shrink: 0;
        }
      }

      .process-btn {
        margin-top: 24px;
        min-width: 200px;
        height: 48px;
        font-size: 16px;
        font-weight: 500;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &__result {
      padding: 0 30px 30px;

      .result-note {
        margin: 8px 0 0;
        font-size: 13px;
        color: #606266;
        
        strong {
          color: #409eff;
        }
      }

      .result-details {
        margin-top: 16px;
      }

      .result-stats {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 16px;

        .stat-item {
          background: #f5f7fa;
          padding: 12px 20px;
          border-radius: 8px;
          text-align: center;
          min-width: 80px;

          .stat-value {
            display: block;
            font-size: 20px;
            font-weight: 600;
            color: #303133;
          }

          .stat-label {
            display: block;
            font-size: 12px;
            color: #909399;
            margin-top: 4px;
          }

          &.success {
            background: #f0f9eb;
            .stat-value { color: #67c23a; }
          }

          &.error {
            background: #fef0f0;
            .stat-value { color: #f56c6c; }
          }

          &.warning {
            background: #fdf6ec;
            .stat-value { color: #e6a23c; }
          }
        }
      }

      .background-note {
        margin-top: 16px;
        padding: 12px 16px;
        background: #ecf5ff;
        border-radius: 8px;
        font-size: 13px;
        color: #409eff;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          font-size: 16px;
        }
      }

      .detail-list {
        margin-top: 16px;
        padding: 16px;
        background: #f5f7fa;
        border-radius: 8px;

        .list-title {
          font-weight: 600;
          font-size: 14px;
          color: #303133;
          margin: 0 0 10px;
        }

        ul {
          margin: 0;
          padding: 0 0 0 20px;

          li {
            font-size: 13px;
            color: #606266;
            margin: 6px 0;
          }
        }

        .accession-list {
          font-size: 13px;
          color: #606266;
          margin: 0;
          word-break: break-all;
          line-height: 1.6;
        }

        &.failed-list {
          background: #fef0f0;
          .list-title { color: #f56c6c; }
        }

        &.not-found-list {
          background: #fdf6ec;
          .list-title { color: #e6a23c; }
        }
      }
    }
  }
}
</style>
